// src/features/caas/hooks/useUserSearch.ts
import { useState, useCallback } from 'react';
import { FabrikUser } from '../../../types';
import { fetchUsers } from '../../../services/api/endpoints/users';
import { useAuth } from '../../../contexts/AuthContext';

interface UseUserSearchResult {
  users: FabrikUser[];
  isSearching: boolean;
  error: string | null;
  search: (query: string) => Promise<void>;
  clear: () => void;
}

export const useUserSearch = (): UseUserSearchResult => {
  const { user } = useAuth();
  const [users, setUsers] = useState<FabrikUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (query: string) => {
    if (!user?.token) {
      setError('No authentication token available');
      return;
    }

    if (!query || query.trim().length === 0) {
      setUsers([]);
      setError(null);
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      
      // Search by email (which also searches name in the backend)
      const result = await fetchUsers(user.token, 1, query);
      setUsers(result.items || []);
    } catch (err: any) {
      console.error('Error searching users:', err);
      setError(err.message || 'Failed to search users');
      setUsers([]);
    } finally {
      setIsSearching(false);
    }
  }, [user?.token]);

  const clear = useCallback(() => {
    setUsers([]);
    setError(null);
  }, []);

  return {
    users,
    isSearching,
    error,
    search,
    clear,
  };
};
